import './settings-leaf.css';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.ts';
import { Button, Checkbox, Select, TextInput } from '../ui.tsx';

interface Settings {
  enabled: boolean;
  emailEnabled: boolean;
  openHours: number;
  graceHours: number;
  reviewUrl: string;
  deliveryActive?: boolean;
}

// `capabilityChecked`: the parent already asked `/job-chats/capability` and only
// mounts this card when job chat is available, so skip asking again.
export default function JobChatSettings({ capabilityChecked = false }: { capabilityChecked?: boolean } = {}) {
  const { t, i18n } = useTranslation();
  const es = i18n.language.startsWith('es');
  const [value, setValue] = useState<Settings | null>(null),
    [notice, setNotice] = useState<'saved' | 'error' | null>(null);
  const saving = useRef(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const load = capabilityChecked
      ? api.getFresh<Settings>('/job-chats/settings')
      : api
          .getFresh<{ enabled: boolean; plan: string }>('/job-chats/capability')
          .then((c) => (c.enabled && c.plan !== 'free' ? api.getFresh<Settings>('/job-chats/settings') : null));
    void load.then(setValue).catch(() => {});
  }, [capabilityChecked]);
  if (!value) return null;
  return (
    <section
      id="job-chat-settings"
      className="card work-section settings-leaf"
      aria-labelledby="job-chat-settings-title"
    >
      <div className="work-section-heading">
        <h2 id="job-chat-settings-title" className="mb-1 text-lg font-semibold">
          {t('jobChat.settings.title')}
        </h2>
      </div>
      <p className="mb-5 max-w-3xl text-sm text-[color:var(--color-muted)]">{t('jobChat.settings.description')}</p>
      <p className="mb-4">
        {es
          ? 'Clientes: desactivados por defecto. Solo un propietario o gerente puede incluir a un cliente para una visita concreta. Los avisos por correo se aplican únicamente a los chats con acceso del cliente aprobado.'
          : 'Customers: off by default. Only an owner or manager can include a customer for an individual job. Email notifications apply only to chats with approved customer access.'}
      </p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (saving.current) return;
          saving.current = true;
          setBusy(true);
          const snapshot = { ...value };
          try {
            await api.post('/job-chats/settings', snapshot);
            setNotice('saved');
          } catch {
            setNotice('error');
          } finally {
            saving.current = false;
            setBusy(false);
          }
        }}
      >
        <fieldset disabled={busy} className="min-w-0 max-w-3xl">
          <legend className="sr-only">{t('jobChat.settings.title')}</legend>
          <div className="space-y-4">
            <Checkbox
              label={t('jobChat.settings.enabled')}
              hint={t('jobChat.settings.enabledHint')}
              checked={value.enabled}
              onChange={(e) => setValue({ ...value, enabled: e.target.checked })}
            />
            <Checkbox
              label={t('jobChat.settings.emailEnabled')}
              hint={
                <>
                  {t('jobChat.settings.emailHint')}
                  {!value.deliveryActive && (
                    <span className="mt-1 block font-medium">{t('jobChat.settings.deliveryInactive')}</span>
                  )}
                </>
              }
              checked={value.emailEnabled}
              onChange={(e) => setValue({ ...value, emailEnabled: e.target.checked })}
            />
          </div>

          <div className="mt-5 border-t border-[color:var(--color-line)] pt-5">
            <h3 className="text-base font-semibold">{t('jobChat.settings.timingTitle')}</h3>
            <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('jobChat.settings.timingHint')}</p>
            <div className="grid gap-x-6 sm:grid-cols-2">
              <Select
                label={t('jobChat.settings.openHours')}
                hint={t('jobChat.settings.openHoursHint')}
                value={value.openHours}
                onChange={(e) => setValue({ ...value, openHours: Number(e.target.value) })}
                options={[12, 24, 48].map((hours) => ({
                  value: String(hours),
                  label: t('jobChat.settings.hours', { count: hours }),
                }))}
              />
              <TextInput
                label={t('jobChat.settings.graceHours')}
                hint={t('jobChat.settings.graceHoursHint')}
                type="number"
                min={6}
                max={72}
                value={value.graceHours}
                onChange={(e) => setValue({ ...value, graceHours: Number(e.target.value) })}
              />
            </div>
          </div>

          <TextInput
            label={t('jobChat.settings.reviewUrl')}
            hint={t('jobChat.settings.reviewUrlHint')}
            type="url"
            placeholder="https://…"
            value={value.reviewUrl}
            onChange={(e) => setValue({ ...value, reviewUrl: e.target.value })}
          />
          <Button type="submit" variant="primary" busy={busy}>
            {t('jobChat.settings.save')}
          </Button>
        </fieldset>
        <p
          role="status"
          className={
            notice
              ? `mt-3 text-sm ${notice === 'error' ? 'text-[color:var(--color-danger)]' : 'text-[color:var(--color-brand)]'}`
              : undefined
          }
        >
          {notice ? t(`jobChat.settings.${notice}`) : null}
        </p>
      </form>
    </section>
  );
}
